import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import LogoLoader from "../components/common/LogoLoader";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";

const AcceptInvite: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  // Invitation validation state
  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState<{
    email: string;
    organization_name: string;
    inviter_name: string;
  } | null>(null);
  const [inviteError, setInviteError] = useState("");

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Validate the invitation token on mount
  useEffect(() => {
    if (!token) {
      setInviteError("No invitation token provided.");
      setValidating(false);
      return;
    }

    authService
      .validateInvite(token)
      .then((data) => {
        setInviteData(data);
      })
      .catch((err: any) => {
        setInviteError(
          err.response?.data?.error ||
            "This invitation is invalid or has expired.",
        );
      })
      .finally(() => {
        setValidating(false);
      });
  }, [token]);

  // If already logged in, redirect
  if (user && !authLoading) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.acceptInvite({
        invitation_token: token,
        password,
        password_confirm: confirmPassword,
        first_name: firstName,
        last_name: lastName,
      });

      // Session cookie is set automatically by the login response
      setAccepted(true);

      // Redirect after a short delay so the user sees the success state
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData) {
        // Handle field-level errors from DRF
        const messages: string[] = [];
        for (const key of Object.keys(errorData)) {
          const val = errorData[key];
          if (Array.isArray(val)) {
            messages.push(...val);
          } else if (typeof val === "string") {
            messages.push(val);
          }
        }
        setError(messages.join(" ") || "Failed to accept invitation.");
      } else {
        setError("Failed to accept invitation. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (validating || authLoading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <LogoLoader size="panel" label="Validating the invitation" />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Validating invitation...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Invalid/expired invitation
  if (inviteError || !inviteData) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{ padding: 4, width: "100%", textAlign: "center" }}
          >
            <Typography variant="h5" color="error" gutterBottom>
              Invitation Not Valid
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {inviteError || "This invitation is invalid or has expired."}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please contact your organization administrator to request a new
              invitation.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Success state
  if (accepted) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{ padding: 4, width: "100%", textAlign: "center" }}
          >
            <Typography variant="h5" color="success.main" gutterBottom>
              Welcome!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You have joined <strong>{inviteData.organization_name}</strong>.
              Redirecting...
            </Typography>
            <LogoLoader size="section" surface="paper" label="Redirecting" sx={{ mt: 2 }} />
          </Paper>
        </Box>
      </Container>
    );
  }

  // Registration form
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{ padding: 4, width: "100%", borderRadius: 3 }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <img
              src="/aa_logo_highres_white_900.png"
              alt="Alpha Affinity Logo"
              style={{
                height: "300px",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Join {inviteData.organization_name}
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {inviteData.inviter_name} has invited you to join their
            organization. Create your account to get started.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Email Address"
              value={inviteData.email}
              disabled
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                margin="normal"
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
              />
            </Box>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating Account..." : "Join Organization"}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{" "}
              <Link
                to="/login"
                style={{ color: "#1976d2", textDecoration: "none" }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AcceptInvite;
